import {
  HttpErrorResponse,
  HttpHandlerFn,
  HttpInterceptorFn,
  HttpRequest,
} from '@angular/common/http';
import { catchError, throwError } from 'rxjs';
import { Router } from '@angular/router';
import { PLATFORM_ID, inject, Injector } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { ToastService } from '../services/toast.service';

let lastNetworkErrorToastTime = 0;
const NETWORK_ERROR_THROTTLE_MS = 5000;

export const httpErrorInterceptor: HttpInterceptorFn = (
  req: HttpRequest<unknown>,
  next: HttpHandlerFn
) => {
  const router = inject(Router);
  const injector = inject(Injector);
  // Resolved here rather than inside catchError, which is not an injection context.
  const isBrowser = isPlatformBrowser(inject(PLATFORM_ID));

  return next(req).pipe(
    catchError((error: HttpErrorResponse) => {
      // Do not process or intercept asset i18n JSON requests to prevent DI cycles
      if (req.url.includes('/assets/i18n/')) {
        return throwError(() => error);
      }

      // Ignore client-aborted / cancelled requests (e.g., component teardown or debounced searches)
      const rawError = (error?.error as any) || error;
      if (
        rawError?.name === 'AbortError' ||
        (rawError instanceof DOMException && rawError.name === 'AbortError') ||
        (error as any)?.type === 'cancel' ||
        error?.message?.includes('aborted')
      ) {
        return throwError(() => error);
      }

      // Everything below is a browser-only side effect: toasts nobody sees and redirects
      // that would rewrite the server-rendered page. Hand the error back to the caller
      // and leave the render alone.
      if (!isBrowser) {
        return throwError(() => error);
      }

      // Lazily resolve services from Injector to prevent circular dependency (NG0200)
      const toastService = injector.get(ToastService);

      // Check if we should skip the global redirect for this specific request
      const skipRedirect = req.headers.has('X-Skip-Error-Redirect');

      // We only want to redirect the whole app for GET requests (critical initial data).
      // POST/PUT/DELETE failures should be handled by the component (e.g., show a toast).
      const isCriticalRequest = req.method === 'GET' && !skipRedirect;

      const isOnline = typeof navigator !== 'undefined' ? navigator.onLine : true;

      if (error.status === 0 || !isOnline) {
        // Skip redirect/toast for assessment screens and offline-enabled flows that handle offline saving themselves
        const currentPath = typeof window !== 'undefined' ? window.location.pathname : router.url;
        const isAssessmentOrOfflineHandled =
          currentPath.includes('tap-test') ||
          currentPath.includes('bmc-type') ||
          currentPath.includes('tier-assessment') ||
          currentPath.includes('teacher-assessment') ||
          currentPath.includes('milestone');

        if (!isAssessmentOrOfflineHandled) {
          const now = Date.now();
          // Throttle network error toasts to avoid spamming multiple identical toasts when concurrent requests fail
          if (now - lastNetworkErrorToastTime > NETWORK_ERROR_THROTTLE_MS) {
            lastNetworkErrorToastTime = now;
            toastService.error('toast.networkError');
          }

          if (!isOnline && isCriticalRequest) {
            router.navigate(['/offline']);
          }
        }
      } else if (error.status === 401) {
        // Clear all stored auth tokens and user data
        if (typeof window !== 'undefined' && window.localStorage) {
          localStorage.removeItem('token');
          localStorage.removeItem('authToken');
          localStorage.removeItem('isLoggedIn');
          localStorage.removeItem('userData');
          localStorage.removeItem('user');
          localStorage.removeItem('role');
          localStorage.removeItem('accessLevel');
          sessionStorage.clear();
        }

        const isLoginRequest =
          req.url.includes('login') || req.url.includes('authenticate');
        const isPublicSelfOnboard = req.url.includes('/self-onboard');
        // The consent endpoint answers 401 when the encrypted link fails to decrypt.
        // The parent has no session to expire, so the page reports it itself.
        const isPublicConsent = req.url.includes('/consent/update');
        const isAlreadyOnLogin =
          router.url.includes('/login') ||
          (typeof window !== 'undefined' && window.location.pathname.includes('/login'));
        const isOnSelfOnboardPage =
          router.url.includes('/self-onboard') ||
          (typeof window !== 'undefined' && window.location.pathname.includes('/self-onboard'));
        const isOnConsentPage =
          router.url.includes('/consent/') ||
          (typeof window !== 'undefined' && window.location.pathname.includes('/consent/'));

        // Public onboarding and consent must not force-redirect to login on auth errors
        if (
          !isLoginRequest &&
          !isAlreadyOnLogin &&
          !isPublicSelfOnboard &&
          !isOnSelfOnboardPage &&
          !isPublicConsent &&
          !isOnConsentPage
        ) {
          // Show session expired toast
          toastService.warning(
            error?.error?.code ? error : 'toast.sessionExpired'
          );

          // Redirect to auth page, optionally with a return URL
          const currentUrl = typeof window !== 'undefined' ? window.location.pathname : router.url;
          const returnUrl = encodeURIComponent(currentUrl);
          router.navigate(['/login'], { queryParams: { returnUrl } });
        }
      } else if (error.status === 403) {
        toastService.warning(error?.error?.code ? error : 'toast.forbidden');
      } else if (error.status >= 500 && isCriticalRequest) {
        // Server error - only show toast for critical GET requests
        toastService.error(error?.error?.code ? error : 'toast.serverError');
      }

      return throwError(() => error);
    })
  );
};
